import { AutoComplete, AutoCompleteOption } from '@/components/ui/autocomplete';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tag } from '@/components/ui/tag';
import {
  ConnectedDeviceFilter,
  ConnectedDeviceFilterChoice,
} from '@/lib/integrationTypes/externalConnectionTypes';
import { ChevronsUpDown } from 'lucide-react';
import React, { useCallback, useState } from 'react';

export interface ExternalFilterProps {
  filter: ConnectedDeviceFilter;
  options: ConnectedDeviceFilterChoice[];
  selected: string | null;
  setSelected: (opts: ConnectedDeviceFilterChoice | null) => void;
  required: boolean;
  extraKeysToInclude: string[];
  disabled?: boolean;
  connectButton?: React.ReactNode;
}

export default function ExternalFilter(props: ExternalFilterProps) {
  return props.filter?.allowFreeText ? (
    <ExternalFilterFreeText {...props} />
  ) : (
    <ExternalFilterComboBox {...props} />
  )
}

function ExternalFilterComboBox({
  filter,
  options,
  selected,
  setSelected,
  extraKeysToInclude,
  required,
  disabled,
  connectButton,
}: ExternalFilterProps) {
  let [open, setOpen] = useState(false);

  return (
    <div>
      <Label htmlFor={filter.key}>
        {filter.displayName}
        {required ? '*' : ''}
      </Label>
      <div className="flex flex-row gap-1">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild disabled={disabled}>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between text-white"
            >
              {selected || 'Select from options...'}
              <ChevronsUpDown className="text-lime" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search devices..." className="h-9" />
              <CommandList>
                <CommandEmpty>No devices found.</CommandEmpty>
                <CommandGroup>
                  {options?.map((option, i) => (
                    <CommandItem
                      key={i}
                      value={option[filter.key]}
                      className="cursor-pointer"
                      onSelect={() => {
                        if (selected != option[filter.key]) {
                          setSelected(option);
                        } else {
                          setSelected(null);
                        }
                        setOpen(false);
                      }}
                    >
                      <div className="flex w-full flex-row justify-between bg-background">
                        <div>{option[filter.key]}</div>
                        <div>
                          {extraKeysToInclude.map((key) => (
                            <Tag variant="success">{option[key]}</Tag>
                          ))}
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Button
          onClick={() => {
            setSelected(null);
          }}
          disabled={selected === null || disabled}
          variant="outline"
        >
          Clear
        </Button>
        {connectButton}
      </div>
    </div>
  );
}

interface DataOption extends AutoCompleteOption {
  data: ConnectedDeviceFilterChoice;
}

function ExternalFilterFreeText({
  filter,
  options,
  selected,
  setSelected,
  extraKeysToInclude,
  required,
  disabled,
  connectButton,
}: ExternalFilterProps) {
  const findOption = useCallback((value: string | null) => {
    if (!value) {
      return null;
    }
    return options.find((option) => option[filter.key] === value) || null;
  }, [options, filter.key]);


  return (
    <div>
      <Label htmlFor={filter.key}>
        {filter.displayName}
        {required ? '*' : ''}
      </Label>
      <div className="flex flex-row gap-1">
        <AutoComplete
          options={options?.map((option) => ({
            label: option[filter.key],
            value: option[filter.key],
            data: option,
            component: (
              <div className="flex w-full flex-row justify-between">
                <div>{option[filter.key]}</div>
                <div>
                  {extraKeysToInclude.map((key) => (
                    <Tag variant="success">{option[key]}</Tag>
                  ))}
                </div>
              </div>
            )
          }))}
          placeholder="Enter a value.."
          isLoading={false}
          className='w-full'
          onValueChange={(value) => {
            setSelected(findOption(value) || (value ? {
              [filter.key]: value,
            } : null));
          }}
          value={selected ?? ""}
          disabled={disabled}
        />
        {(required &&
          <Button
            onClick={() => {
              setSelected(null);
            }}
            disabled={selected === null || disabled}
            variant="outline"
          >
            Clear
          </Button>
        )}
        {connectButton}
      </div>
    </div>
  );
}
